import { useEffect, useState } from 'react';
import { ref as rtdbRef, onValue } from 'firebase/database';
import { rtdb } from '../services/firebase';
import { useAuth } from './useAuth';

interface RtdbAdminState {
  /** True only when /admins/{uid} === true in RTDB. Fails safe to false. */
  isAdmin: boolean;
  /** True while we don't yet have an authoritative answer for the current user. */
  loading: boolean;
}

/**
 * Live admin gate backed by the RTDB `/admins/{uid}` marker — the same node the
 * RTDB rules consult to authorize writes to `/configuration/*`. We subscribe
 * with `onValue` so admin grants/revokes (and mid-session sign-in) propagate
 * without a rebuild.
 *
 * Fails safe: no signed-in user, a missing node, or a read error all resolve to
 * `isAdmin: false` so the UI disables rather than enables.
 */
export function useIsRtdbAdmin(): RtdbAdminState {
  const { user, loading: authLoading } = useAuth();
  const uid = user?.uid ?? null;

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    // No signed-in user yet → not an admin. Still "loading" only while auth is.
    if (!uid) {
      setIsAdmin(false);
      setAdminLoading(authLoading);
      return;
    }

    setAdminLoading(true);
    const adminRef = rtdbRef(rtdb, `/admins/${uid}`);
    const unsubscribe = onValue(
      adminRef,
      (snap) => {
        setIsAdmin(snap.val() === true);
        setAdminLoading(false);
      },
      () => {
        // Read failed (network, rules, etc.) → fail safe to not-admin.
        setIsAdmin(false);
        setAdminLoading(false);
      },
    );

    return unsubscribe;
  }, [uid, authLoading]);

  return { isAdmin, loading: adminLoading };
}
